import React from 'react';

import { useVideoEmbedQuery, Video } from '@edx/graphql/generated';
import { useAuth } from '@edx/hooks/use-auth';
import { IconAngleLeft, IconAngleRight } from '@edx/components/icons';
import { Link } from '@edx/components/link';
import { Caption } from '@edx/components/typography';

import { Player, PlayerPlaceholder, PlayerRestricted, ProspectLifecycle } from './components';
import { useRouter } from 'next/router';

type Props = {
  video: Video;
  nextVideo?: Video;
  prevVideo?: Video;
};

export const VideoPlayer: React.FC<Props> = ({ video, nextVideo, prevVideo }) => {
  const router = useRouter();
  const signature = typeof router?.query?.signature === 'string' ? router?.query?.signature : undefined;
  const { isLoggedOut } = useAuth();
  const { data, loading } = useVideoEmbedQuery({
    variables: { slug: video.slug, courseSlug: video.course.slug, signature },
  });

  const embedId = data?.video?.embedId;

  const thumbnail = { src: video.imageUrl as string, alt: `${video.course.name} - ${video.name}` };
  const showProspectLifecycle = isLoggedOut && video.isPublic && !loading && !embedId;

  if (showProspectLifecycle) return <ProspectLifecycle videoId={video.id} thumbnail={thumbnail} />;

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      <div className="px-0 lg:px-4">
        <div className="relative bg-gray-800 w-full max-w-full">
          {loading ? (
            <PlayerPlaceholder thumbnail={thumbnail} />
          ) : embedId ? (
            <Player videoId={video.id} embedId={embedId as number} />
          ) : (
            <PlayerRestricted thumbnail={thumbnail} />
          )}
        </div>
      </div>

      <div className="absolute w-full inset-y-1/2">
        <div className="relative">
          {prevVideo && (
            <Link
              href={prevVideo.resourcePath}
              className="absolute ease-in-out flex items-center group h-14 hover:bg-gray-800 items-center justify-end p-3 left-0 transform translate-x-0 lg:-translate-x-9"
            >
              <IconAngleLeft className="w-7 h-7 text-gray-300 text-opacity-30 group-hover:text-opacity-100" />

              <div className="hidden group-hover:block w-60 min-w-60 max-w-72 px-2">
                <Caption size="small" variant="semibold" className="text-gray-300">
                  {prevVideo.name}
                </Caption>
              </div>
            </Link>
          )}
          {nextVideo && (
            <Link
              href={nextVideo.resourcePath}
              className="absolute ease-in-out flex items-center group h-14 hover:bg-gray-800 items-center justify-end p-3 right-0 transform translate-x-0 lg:translate-x-9"
            >
              <div className="hidden group-hover:block w-60 min-w-60 max-w-72 px-2">
                <Caption size="small" variant="semibold" className="text-gray-300">
                  {nextVideo.name}
                </Caption>
              </div>

              <IconAngleRight className="w-7 h-7 text-gray-300 text-opacity-30 group-hover:text-opacity-100" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
